import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, Power, Calendar, Users, Activity, Database, Shield, KeyRound } from 'lucide-react';
import apiService from '../apiService';

/**
 * Mode maintenance — superadmin et rôle IT uniquement.
 * Interrupteurs global + par app, message, fin estimée, planification avec
 * annonce, comptes testeurs, et compteur des activités encore en cours
 * (elles peuvent se terminer ; les nouvelles demandes sont bloquées).
 */
const APPS = [
  ['client', '📱 App Client'],
  ['driver', '🚗 App Chauffeur'],
  ['business', '🏪 App Business'],
  ['web', '🌐 Sites web'],
];

// Date ISO ⇄ valeur d'un <input type="datetime-local"> (heure locale)
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export default function MaintenancePage() {
  const [data, setData] = useState(null);       // { state, effective, schedule_active, live }
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);         // { ok, text }

  // Champs éditables
  const [message, setMessage] = useState('');
  const [eta, setEta] = useState('');
  const [schStart, setSchStart] = useState('');
  const [schEnd, setSchEnd] = useState('');
  const [schAnnounce, setSchAnnounce] = useState(3);
  const [schScope, setSchScope] = useState(['global']);
  const [testerInput, setTesterInput] = useState('');

  const hydrate = (d) => {
    setData(d);
    setMessage(d.state.message || '');
    setEta(toLocalInput(d.state.eta));
    const s = d.state.schedule;
    setSchStart(toLocalInput(s?.start)); setSchEnd(toLocalInput(s?.end));
    setSchAnnounce(s?.announce_hours ?? 3); setSchScope(s?.scope || ['global']);
  };

  const load = useCallback(async () => {
    try {
      const r = await apiService.get('/admin/maintenance', { userType: 'admin' });
      hydrate(r);
    } catch (e) { setMsg({ ok: false, text: e.message || 'Chargement impossible' }); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Compteur des activités en cours rafraîchi
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const save = async (patch, okText) => {
    setSaving(true); setMsg(null);
    try {
      const r = await apiService.put('/admin/maintenance', patch, { userType: 'admin' });
      hydrate(r);
      setMsg({ ok: true, text: okText || 'Enregistré ✅' });
    } catch (e) { setMsg({ ok: false, text: e.message || 'Erreur' }); }
    finally { setSaving(false); }
  };

  const toggle = (key) => {
    const cur = data.state.manual[key];
    const label = key === 'global' ? 'TOUTES les apps' : APPS.find(a => a[0] === key)?.[1];
    if (!cur && !window.confirm(`Activer la maintenance pour ${label} ?\n\nLes nouvelles commandes et courses seront bloquées immédiatement. Les activités en cours pourront se terminer.`)) return;
    save({ manual: { [key]: !cur } }, !cur ? `Maintenance activée : ${label}` : `Maintenance désactivée : ${label}`);
  };

  const saveSchedule = () => {
    if (!schStart || !schEnd) return setMsg({ ok: false, text: 'Indiquez le début et la fin.' });
    save({ schedule: { start: fromLocalInput(schStart), end: fromLocalInput(schEnd), announce_hours: Number(schAnnounce) || 0, scope: schScope } },
      'Maintenance planifiée ✅');
  };

  const addTester = () => {
    const t = testerInput.trim();
    if (!t) return;
    save({ testers: [...(data.state.testers || []), t] }, 'Testeur ajouté');
    setTesterInput('');
  };

  if (!data) return <div className="dashboard-content"><p>Chargement…</p></div>;

  const { state, effective, schedule_active: schActive, live } = data;
  const anyOn = Object.values(effective).some(Boolean);

  const card = { background: 'white', borderRadius: 16, padding: '1.25rem 1.5rem', marginBottom: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };
  const label = { fontSize: '0.82rem', fontWeight: 700, color: '#5C636E', marginBottom: 6, display: 'block' };
  const input = { width: '100%', padding: '9px 12px', border: '1px solid #DCE0E6', borderRadius: 9, fontSize: '0.92rem', boxSizing: 'border-box' };
  const btn = (bg, color = '#fff') => ({ padding: '9px 18px', background: bg, color, border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' });

  const Switch = ({ on, onClick, big }) => (
    <button onClick={onClick} disabled={saving} aria-pressed={on}
      style={{ width: big ? 58 : 46, height: big ? 32 : 26, borderRadius: 20, border: 'none', cursor: 'pointer', position: 'relative', background: on ? '#DC2626' : '#D1D5DB', transition: 'background .2s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? (big ? 29 : 23) : 3, width: big ? 26 : 20, height: big ? 26 : 20, borderRadius: '50%', background: 'white', transition: 'left .2s' }} />
    </button>
  );

  return (
    <div className="dashboard-content" style={{ maxWidth: 900 }}>
      <h1 className="page-title"><Wrench size={22} style={{ verticalAlign: '-4px' }} /> Mode maintenance</h1>

      {msg && <div style={{ padding: '10px 16px', borderRadius: 10, marginBottom: '1rem', fontWeight: 600, background: msg.ok ? '#ECFDF5' : '#FEF2F2', color: msg.ok ? '#047857' : '#B91C1C' }}>{msg.text}</div>}

      {/* État + activités en cours */}
      <div style={{ ...card, display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap', borderLeft: `5px solid ${anyOn ? '#DC2626' : '#10B981'}` }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: anyOn ? '#B91C1C' : '#047857' }}>
            {anyOn ? '🔴 Maintenance ACTIVE' : '🟢 Plateforme ouverte'}
          </div>
          {anyOn && (
            <div style={{ fontSize: '0.85rem', color: '#5C636E', marginTop: 4 }}>
              {effective.global ? 'Toutes les apps' : APPS.filter(([k]) => effective[k]).map(a => a[1]).join(' · ')}
              {schActive ? ' (planifiée)' : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#414852' }}>
          <Activity size={18} />
          <span style={{ fontSize: '0.88rem' }}>
            En cours : <b>{live.rides}</b> course(s) · <b>{live.orders}</b> livraison(s) · <b>{live.parcels}</b> colis
          </span>
        </div>
      </div>

      {/* Interrupteurs */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.25rem' }}><Power size={17} style={{ verticalAlign: '-3px' }} /> Activation immédiate</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#9AA0A8' }}>
          Nouvelles commandes et courses bloquées tout de suite ; les activités en cours peuvent se terminer.
          Le dashboard admin, les connexions et les paiements Mobile Money ne sont jamais coupés.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '2px solid #F4F5F7' }}>
          <b>🌍 Toutes les apps</b>
          <Switch big on={state.manual.global} onClick={() => toggle('global')} />
        </div>
        {APPS.map(([k, l]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: '1px solid #F4F5F7' }}>
            <span>{l}{!state.manual[k] && effective[k] ? <span style={{ color: '#B91C1C', fontSize: '0.8rem', fontWeight: 700 }}> — active (global ou planifiée)</span> : null}</span>
            <Switch on={state.manual[k]} onClick={() => toggle(k)} />
          </div>
        ))}
      </div>

      {/* Message */}
      <div style={card}>
        <h3 style={{ margin: '0 0 1rem' }}>Message affiché aux utilisateurs</h3>
        <span style={label}>Message</span>
        <textarea rows={3} style={{ ...input, resize: 'vertical' }} value={message} onChange={e => setMessage(e.target.value)} maxLength={300}
          placeholder="Ex. Nous améliorons Mbiyo pour vous. Retour prévu vers 2 h du matin. Merci de votre patience !" />
        <span style={{ ...label, marginTop: 12 }}>Retour prévu (facultatif)</span>
        <input type="datetime-local" style={{ ...input, maxWidth: 280 }} value={eta} onChange={e => setEta(e.target.value)} />
        <div style={{ marginTop: 14 }}>
          <button style={btn('#1C1C1C')} disabled={saving} onClick={() => save({ message, eta: fromLocalInput(eta) }, 'Message enregistré')}>Enregistrer le message</button>
        </div>
      </div>

      {/* Planification */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.25rem' }}><Calendar size={17} style={{ verticalAlign: '-3px' }} /> Maintenance planifiée</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#9AA0A8' }}>
          S'active et se désactive toute seule. Un bandeau d'annonce s'affiche dans les apps avant le début.
        </p>
        {state.schedule && (
          <div style={{ background: schActive ? '#FEF2F2' : '#FFFBEB', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.88rem' }}>
            {schActive ? '🔴 En cours' : '🗓 Programmée'} : du <b>{fmt(state.schedule.start)}</b> au <b>{fmt(state.schedule.end)}</b>
            {' · '}annonce {state.schedule.announce_hours} h avant
            {' · '}{state.schedule.scope.includes('global') ? 'toutes les apps' : state.schedule.scope.map(s => APPS.find(a => a[0] === s)?.[1]).join(', ')}
            <button style={{ ...btn('transparent', '#B91C1C'), padding: '0 0 0 12px' }} disabled={saving}
              onClick={() => window.confirm('Annuler cette maintenance planifiée ?') && save({ schedule: null }, 'Planification annulée')}>Annuler</button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div><span style={label}>Début</span><input type="datetime-local" style={input} value={schStart} onChange={e => setSchStart(e.target.value)} /></div>
          <div><span style={label}>Fin</span><input type="datetime-local" style={input} value={schEnd} onChange={e => setSchEnd(e.target.value)} /></div>
          <div><span style={label}>Annonce (heures avant)</span><input type="number" min={0} max={72} style={input} value={schAnnounce} onChange={e => setSchAnnounce(e.target.value)} /></div>
        </div>
        <span style={{ ...label, marginTop: 12 }}>Apps concernées</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['global', '🌍 Toutes'], ...APPS].map(([k, l]) => {
            const on = schScope.includes(k);
            return (
              <button key={k} onClick={() => setSchScope(k === 'global' ? ['global'] : on ? schScope.filter(x => x !== k) : [...schScope.filter(x => x !== 'global'), k])}
                style={{ padding: '7px 13px', borderRadius: 16, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', border: on ? '1.5px solid #F54119' : '1px solid #DCE0E6', background: on ? '#FEE9E2' : 'white', color: on ? '#C9350F' : '#5C636E' }}>
                {l}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 14 }}>
          <button style={btn('#F54119')} disabled={saving} onClick={saveSchedule}>Programmer la maintenance</button>
        </div>
      </div>

      {/* Testeurs */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.25rem' }}><Users size={17} style={{ verticalAlign: '-3px' }} /> Comptes testeurs</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#9AA0A8' }}>
          Ces numéros (client ou chauffeur) utilisent l'app normalement pendant la maintenance, pour vérifier avant la réouverture.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {(state.testers || []).length === 0 && <span style={{ color: '#9AA0A8', fontSize: '0.88rem' }}>Aucun testeur.</span>}
          {(state.testers || []).map(t => (
            <span key={t} style={{ background: '#F4F5F7', borderRadius: 16, padding: '5px 12px', fontSize: '0.85rem', fontWeight: 600 }}>
              {t}
              <button onClick={() => save({ testers: state.testers.filter(x => x !== t) }, 'Testeur retiré')} disabled={saving}
                style={{ marginLeft: 8, border: 'none', background: 'none', cursor: 'pointer', color: '#B91C1C', fontWeight: 800 }}>×</button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, maxWidth: 420 }}>
          <input style={input} value={testerInput} onChange={e => setTesterInput(e.target.value)} placeholder="+243 99 123 4567"
            onKeyDown={e => e.key === 'Enter' && addTester()} />
          <button style={btn('#1C1C1C')} disabled={saving} onClick={addTester}>Ajouter</button>
        </div>
      </div>

      <BackupCard card={card} btn={btn} />
      <DemoAccountsCard card={card} btn={btn} />
      <RateLimitCard card={card} />

      {state.updated_at && (
        <p style={{ fontSize: '0.78rem', color: '#9AA0A8' }}>Dernière modification : {fmt(state.updated_at)} par {state.updated_by}</p>
      )}
    </div>
  );
}

// ── Sauvegarde de la base de données (étape V3) ───────────────────────────────
const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round((n || 0) / 1024))} Ko`);
const KIND = { daily: 'Quotidienne', monthly: 'Mensuelle', manual: 'Manuelle' };

function BackupCard({ card, btn }) {
  const [info, setInfo] = useState(null);       // { last, running, files }
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);       // { ok, text }
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try { setInfo(await apiService.get('/admin/maintenance/backups', { userType: 'admin' })); }
    catch (e) { setNote({ ok: false, text: e.message || 'Chargement impossible' }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await apiService.post('/admin/maintenance/backups', {}, { userType: 'admin' });
      setNote({ ok: true, text: `Sauvegarde terminée : ${r.summary.documents} documents, ${kb(r.summary.size)}.` });
      load();
    } catch (e) { setNote({ ok: false, text: e.message || 'La sauvegarde a échoué' }); }
    finally { setBusy(false); }
  };

  const last = info?.last;
  const ageH = last?.at ? (Date.now() - new Date(last.at).getTime()) / 3600000 : null;
  const healthy = ageH !== null && ageH <= 26;
  const files = info?.files || [];
  const shown = showAll ? files : files.slice(0, 6);

  return (
    <div style={{ ...card, borderLeft: `5px solid ${info ? (healthy ? '#10B981' : '#DC2626') : '#DCE0E6'}` }}>
      <h3 style={{ margin: '0 0 0.25rem' }}><Database size={17} style={{ verticalAlign: '-3px' }} /> Sauvegarde de la base de données</h3>
      <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#9AA0A8' }}>
        Copie complète chaque nuit vers 2 h (heure de Kinshasa), dans le stockage privé Google Cloud.
        Conservées 30 jours, plus une par mois gardée 12 mois. Un échec déclenche une alerte (Administration → Erreurs + e-mail).
      </p>
      {note && <div style={{ padding: '8px 14px', borderRadius: 10, marginBottom: 12, fontWeight: 600, fontSize: '0.88rem', background: note.ok ? '#ECFDF5' : '#FEF2F2', color: note.ok ? '#047857' : '#B91C1C' }}>{note.text}</div>}
      {!info ? <p style={{ color: '#9AA0A8' }}>Chargement…</p> : (
        <>
          <div style={{ fontSize: '0.92rem', marginBottom: 12 }}>
            {last?.at ? (
              <>
                {healthy ? '🟢' : '🔴'} Dernière sauvegarde réussie : <b>{fmt(last.at)}</b> ({KIND[last.trigger === 'nightly' ? 'daily' : last.trigger] || last.trigger})
                {' · '}{last.documents} documents · {last.collections} collections · {kb(last.size)}
                {!healthy && <div style={{ color: '#B91C1C', fontWeight: 700, marginTop: 4 }}>Aucune sauvegarde réussie depuis plus de 26 heures.</div>}
              </>
            ) : <span style={{ color: '#B91C1C', fontWeight: 700 }}>🔴 Aucune sauvegarde enregistrée pour l'instant.</span>}
          </div>
          <button style={btn('#1C1C1C')} disabled={busy || info.running} onClick={runNow}>
            {busy || info.running ? 'Sauvegarde en cours…' : 'Lancer une sauvegarde maintenant'}
          </button>
          {files.length > 0 && (
            <div style={{ marginTop: 16, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#5C636E' }}>
                    <th style={{ padding: '6px 8px' }}>Date</th><th style={{ padding: '6px 8px' }}>Type</th>
                    <th style={{ padding: '6px 8px' }}>Taille</th><th style={{ padding: '6px 8px' }}>Fichier</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(f => (
                    <tr key={f.path} style={{ borderTop: '1px solid #F4F5F7' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmt(f.created_at)}</td>
                      <td style={{ padding: '6px 8px' }}>{KIND[f.path.split('/')[1]] || '—'}</td>
                      <td style={{ padding: '6px 8px', fontVariantNumeric: 'tabular-nums' }}>{kb(f.size)}</td>
                      <td style={{ padding: '6px 8px', color: '#9AA0A8', fontFamily: 'monospace', fontSize: '0.78rem' }}>{f.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {files.length > 6 && (
                <button style={{ ...btn('transparent', '#C9350F'), padding: '8px 0 0' }} onClick={() => setShowAll(v => !v)}>
                  {showAll ? 'Afficher moins' : `Afficher les ${files.length} sauvegardes`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Limites anti-abus : mesure des refus (2026-10-29, « mesurer d'abord ») ──────────────
const LIMITER_LABEL = {
  auth: 'Connexion / compte (10 par 15 min et par adresse)',
  otp_send: 'Envoi de code WhatsApp (12 par 15 min et par adresse)',
  otp_verify: 'Saisie du code (40 par 15 min et par adresse)',
  reset_password: 'Mot de passe oublié (10 par 15 min et par adresse)',
  global: 'Toutes requêtes (1 200 par 15 min et par utilisateur)',
};

function RateLimitCard({ card }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiService.get('/admin/maintenance/rate-limits', { userType: 'admin' })
      .then((r) => setRows(r.rows || []))
      .catch((e) => setErr(e.message || 'Chargement impossible'));
  }, []);
  const blocked = (rows || []).reduce((s, r) => s + (r.blocked || 0), 0);
  const cell = { padding: '6px 8px', borderTop: '1px solid #F1F2F4' };
  return (
    <div style={{ ...card, borderLeft: `5px solid ${rows ? (blocked ? '#F59E0B' : '#10B981') : '#DCE0E6'}` }}>
      <h3 style={{ margin: '0 0 0.25rem' }}><Shield size={17} style={{ verticalAlign: '-3px' }} /> Limites anti-abus (14 derniers jours)</h3>
      <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#9AA0A8' }}>
        Les opérateurs font sortir beaucoup d'abonnés par la même adresse IP : une limite par adresse peut bloquer de vrais utilisateurs.
        On mesure avant de changer : refus, adresses et numéros distincts (anonymisés), et pic d'utilisation d'une fenêtre de 15 minutes.
        « Près de la limite » = adresses arrivées à 80 % sans être refusées.
      </p>
      {err && <p style={{ color: '#B91C1C', fontWeight: 600 }}>{err}</p>}
      {!rows && !err ? <p style={{ color: '#9AA0A8' }}>Chargement…</p> : null}
      {rows && rows.length === 0 ? <p style={{ color: '#047857', fontWeight: 600 }}>🟢 Aucune donnée pour l'instant (la mesure commence au déploiement du 29 octobre 2026).</p> : null}
      {rows && rows.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#5C636E' }}>
                <th style={{ padding: '6px 8px' }}>Jour</th><th style={{ padding: '6px 8px' }}>Limite</th>
                <th style={{ padding: '6px 8px' }}>Requêtes</th><th style={{ padding: '6px 8px' }}>Refus</th>
                <th style={{ padding: '6px 8px' }}>Adresses refusées</th><th style={{ padding: '6px 8px' }}>Numéros visés</th>
                <th style={{ padding: '6px 8px' }}>Pic</th><th style={{ padding: '6px 8px' }}>Près de la limite</th><th style={{ padding: '6px 8px' }}>Apps</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.day}-${r.limiter}`} style={{ background: r.blocked ? '#FFFBEB' : 'transparent' }}>
                  <td style={cell}>{r.day}</td>
                  <td style={cell}>{LIMITER_LABEL[r.limiter] || r.limiter}</td>
                  <td style={cell}>{(r.requests || 0).toLocaleString('fr-FR')}</td>
                  <td style={{ ...cell, fontWeight: r.blocked ? 700 : 400, color: r.blocked ? '#B45309' : 'inherit' }}>{r.blocked || 0}</td>
                  <td style={cell}>{r.blocked_ips || 0}</td>
                  <td style={cell}>{r.blocked_numbers || 0}</td>
                  <td style={cell}>{r.peak_used || 0}{r.limit ? ` / ${r.limit}` : ''}</td>
                  <td style={cell}>{r.near_limit_ips || 0}</td>
                  <td style={cell}>{Object.entries(r.blocked_by_app || {}).map(([k, v]) => `${k} ${v}`).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

// ── Comptes de démonstration pour les examinateurs des stores (2026-10-30) ──────────────
function DemoAccountsCard({ card, btn }) {
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiService.get('/admin/maintenance/demo-accounts', { userType: 'admin' })
      .then((r) => setRows(r.accounts || []))
      .catch((e) => setNote({ ok: false, text: e.message || 'Chargement impossible' }));
  }, []);
  const update = (i, k, v) => setRows((list) => list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const randomCode = () => String(Math.floor(100000 + Math.random() * 900000));
  const save = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await apiService.request('/admin/maintenance/demo-accounts', { method: 'PUT', userType: 'admin', body: JSON.stringify({ accounts: rows }) });
      setRows(r.accounts || []);
      setNote({ ok: true, text: 'Liste enregistrée. Effet immédiat (au plus 30 secondes).' });
    } catch (e) { setNote({ ok: false, text: e.message || 'Enregistrement impossible' }); }
    finally { setBusy(false); }
  };
  const input = { padding: '8px 10px', border: '1px solid #DCE0E6', borderRadius: 8, fontSize: '0.9rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  return (
    <div style={{ ...card, borderLeft: '5px solid #7A3BB0' }}>
      <h3 style={{ margin: '0 0 0.25rem' }}><KeyRound size={17} style={{ verticalAlign: '-3px' }} /> Comptes de démonstration (examinateurs des stores)</h3>
      <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#9AA0A8' }}>
        Les examinateurs Apple et Google ne reçoivent pas de code WhatsApp. Pour les numéros ci-dessous, rien n'est envoyé et le
        <b> code fixe</b> est le seul accepté ; tout le reste (création du compte, session, PIN) est identique au vrai. Utilisez des numéros
        qui n'appartiennent à personne, changez le code après chaque soumission, jamais d'argent réel sur ces comptes.
        Le compte lui-même se crée en se connectant dans l'app avec ce numéro et ce code (puis validation chauffeur / commerce dans l'admin).
      </p>
      {note && <div style={{ padding: '8px 14px', borderRadius: 10, marginBottom: 12, fontWeight: 600, fontSize: '0.88rem', background: note.ok ? '#ECFDF5' : '#FEF2F2', color: note.ok ? '#047857' : '#B91C1C' }}>{note.text}</div>}
      {!rows ? <p style={{ color: '#9AA0A8' }}>Chargement…</p> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 1.4fr auto', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: '0.78rem', color: '#5C636E', fontWeight: 600 }}>
            <span>Numéro (international)</span><span>Code fixe (6 chiffres)</span><span>Usage</span><span />
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 1.4fr auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input style={input} value={r.phone || ''} onChange={(e) => update(i, 'phone', e.target.value)} placeholder="+243 9xx xxx xxx" />
              <div style={{ display: 'flex', gap: 4 }}>
                <input style={{ ...input, fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: 2 }} value={r.code || ''} onChange={(e) => update(i, 'code', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" />
                <button type="button" title="Nouveau code" onClick={() => update(i, 'code', randomCode())} style={{ ...btn('#F4F5F7', '#1C1C1C'), padding: '6px 10px' }}>↻</button>
              </div>
              <input style={input} value={r.label || ''} onChange={(e) => update(i, 'label', e.target.value)} placeholder="Examinateur app client" />
              <button type="button" onClick={() => setRows((list) => list.filter((_, j) => j !== i))} style={{ ...btn('#FEE2E2', '#B91C1C'), padding: '6px 10px' }}>Retirer</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button type="button" disabled={rows.length >= 10} onClick={() => setRows((list) => [...list, { phone: '+243', code: randomCode(), label: '' }])} style={btn('#F4F5F7', '#1C1C1C')}>+ Ajouter un numéro</button>
            <button type="button" disabled={busy} onClick={save} style={btn('#7A3BB0')}>{busy ? 'Enregistrement…' : 'Enregistrer la liste'}</button>
          </div>
        </>
      )}
    </div>
  );
}
